import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Building2, Loader2, FileText, File, Image, FileSpreadsheet, 
  Eye, Download, AlertCircle, Info, FolderOpen, RefreshCw, Search,
  Plus, Upload, X, Trash2, Edit, Users, User, Calendar
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ShubeBilgileri = () => {
  const [cards, setCards] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [activeTab, setActiveTab] = useState('bilgiler');
  
  // Belgeler için state
  const [documentForms, setDocumentForms] = useState([]);
  const [personelForms, setPersonelForms] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  // Arama state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Şube Yöneticisi kontrolü
  const [isBranchManager, setIsBranchManager] = useState(false);
  const [branchEmployees, setBranchEmployees] = useState([]);
  
  // Bilgi Kartı Ekleme Modal
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [cardFormData, setCardFormData] = useState({
    title: '',
    description: '',
    publish_date: new Date().toISOString().split('T')[0],
    recipient_type: 'all', // 'all' veya 'specific'
    recipient_ids: [],
    files: []
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [savingCard, setSavingCard] = useState(false);

  useEffect(() => {
    checkUserRole();
    loadCards();
    loadDocumentForms();
    loadPersonelForms();
    // Sayfa açıldığında tüm kartları görüldü olarak işaretle
    markAllCardsSeen();
  }, []);

  const markAllCardsSeen = async () => {
    try {
      await axios.post(`${API}/my/branch-info-cards/mark-all-seen`, {}, { headers: getAuthHeaders() });
      // WorkPanel'deki badge'i güncellemesi için event gönder
      window.dispatchEvent(new Event('refreshInfoCardsCount'));
    } catch (error) {
      console.error('Kartlar görüldü işaretlenemedi:', error);
    }
  };

  const checkUserRole = () => {
    try {
      const userStr = localStorage.getItem('user') || localStorage.getItem('fortis_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        // panel_access array veya string olabilir
        const panelAccess = user.panel_access;
        const hasBranchAccess = Array.isArray(panelAccess) 
          ? panelAccess.includes('branch') 
          : panelAccess === 'branch';
        setIsBranchManager(hasBranchAccess);
        
        if (hasBranchAccess) {
          loadBranchEmployees();
        }
      }
    } catch (e) {
      console.error('Kullanıcı bilgisi okunamadı:', e);
    }
  };

  const loadBranchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/branch/employees`, { headers: getAuthHeaders() });
      setBranchEmployees(res.data || []);
    } catch (error) {
      console.error('Şube çalışanları yüklenemedi:', error);
    }
  };

  const loadCards = async () => {
    try {
      const res = await axios.get(`${API}/my/branch-info-cards`, { headers: getAuthHeaders() });
      setCards(res.data?.cards || []);
      setBranchName(res.data?.branch_name || '');
    } catch (error) {
      console.error('Kartlar yüklenirken hata:', error);
      toast.error('Şube bilgileri yüklenemedi');
    }
    setLoading(false);
  };

  // Kartı görüldü olarak işaretle
  const markCardAsSeen = async (card) => {
    if (!card.is_new) return;
    
    try {
      await axios.post(`${API}/my/branch-info-cards/${card.id}/mark-seen`, {}, { headers: getAuthHeaders() });
      // Kartı güncelle
      setCards(prev => prev.map(c => c.id === card.id ? { ...c, is_new: false } : c));
    } catch (error) {
      console.error('Kart görüldü işaretlenemedi:', error);
    }
  };

  // Bilgi Kartı Ekleme/Düzenleme Fonksiyonları
  const openCreateCardModal = () => {
    setEditingCard(null);
    setCardFormData({
      title: '',
      description: '',
      publish_date: new Date().toISOString().split('T')[0],
      recipient_type: 'all',
      recipient_ids: [],
      files: []
    });
    setPendingFiles([]);
    setCardModalOpen(true);
  };

  const openEditCardModal = (card) => {
    setEditingCard(card);
    setCardFormData({
      title: card.title || '',
      description: card.description || '',
      publish_date: card.publish_date || new Date().toISOString().split('T')[0],
      recipient_type: card.recipient_type || 'all',
      recipient_ids: card.recipient_ids || [],
      files: card.files || []
    });
    setPendingFiles([]);
    setCardModalOpen(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPendingFiles(prev => [...prev, {
          name: file.name,
          type: file.type,
          size: file.size,
          data: event.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index) => {
    setCardFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const toggleRecipient = (userId) => {
    setCardFormData(prev => {
      const newIds = prev.recipient_ids.includes(userId)
        ? prev.recipient_ids.filter(id => id !== userId)
        : [...prev.recipient_ids, userId];
      return { ...prev, recipient_ids: newIds };
    });
  };

  const handleSaveCard = async () => {
    if (!cardFormData.title.trim()) {
      toast.error('Konu alanı zorunludur');
      return;
    }

    setSavingCard(true);
    try {
      const payload = {
        title: cardFormData.title,
        description: cardFormData.description,
        publish_date: cardFormData.publish_date,
        recipient_type: cardFormData.recipient_type,
        recipient_ids: cardFormData.recipient_type === 'specific' ? cardFormData.recipient_ids : [],
        files: [...cardFormData.files, ...pendingFiles]
      };

      if (editingCard) {
        await axios.put(`${API}/branch/info-cards/${editingCard.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Bilgi kartı güncellendi');
      } else {
        await axios.post(`${API}/branch/info-cards`, payload, { headers: getAuthHeaders() });
        toast.success('Bilgi kartı oluşturuldu');
      }

      setCardModalOpen(false);
      loadCards();
    } catch (error) {
      console.error('Kart kaydedilemedi:', error);
      toast.error(error.response?.data?.detail || 'Bilgi kartı kaydedilemedi');
    }
    setSavingCard(false);
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Bu bilgi kartını silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/branch/info-cards/${cardId}`, { headers: getAuthHeaders() });
      toast.success('Bilgi kartı silindi');
      loadCards();
    } catch (error) {
      console.error('Kart silinemedi:', error);
      toast.error('Bilgi kartı silinemedi');
    }
  };

  const loadDocumentForms = async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/employee/document-requests`, { headers: getAuthHeaders() });
      setDocumentForms(res.data || []);
    } catch (error) {
      console.error('Belge formları yüklenirken hata:', error);
    }
    setLoadingDocs(false);
  };

  const loadPersonelForms = async () => {
    try {
      const res = await axios.get(`${API}/public/personel-forms`, { headers: getAuthHeaders() });
      setPersonelForms(res.data || []);
    } catch (error) {
      console.error('Personel formları yüklenirken hata:', error);
    }
  };

  const getFileIcon = (type) => {
    if (type?.includes('image')) return <Image className="h-5 w-5 text-green-600" />;
    if (type?.includes('pdf')) return <FileText className="h-5 w-5 text-red-600" />;
    if (type?.includes('spreadsheet') || type?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    return <File className="h-5 w-5 text-blue-600" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    // Dosya data URL olarak veya normal URL olarak gelebilir
    link.href = file.data || file.url;
    link.download = file.filename || file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Dosya indiriliyor...');
  };

  // Filtrelenmiş belgeler
  const filteredPersonelForms = useMemo(() => {
    if (!searchTerm.trim()) return personelForms;
    const searchLower = searchTerm.toLowerCase();
    return personelForms.filter(form =>
      form.title?.toLowerCase().includes(searchLower) ||
      form.description?.toLowerCase().includes(searchLower) ||
      form.created_by_name?.toLowerCase().includes(searchLower)
    );
  }, [personelForms, searchTerm]);

  const filteredDocumentForms = useMemo(() => {
    if (!searchTerm.trim()) return documentForms;
    const searchLower = searchTerm.toLowerCase();
    return documentForms.filter(form =>
      form.title?.toLowerCase().includes(searchLower) ||
      form.description?.toLowerCase().includes(searchLower) ||
      form.created_by_name?.toLowerCase().includes(searchLower)
    );
  }, [documentForms, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sube-bilgileri-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sube-bilgileri">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-600" />
            Şube Bilgileri
          </h1>
          <p className="text-gray-500">
            {branchName ? `${branchName} şubesine ait bilgiler ve belgeler` : 'Şubenize ait bilgiler ve belgeler'}
          </p>
        </div>
        <div className="flex gap-2">
          {isBranchManager && (
            <Button onClick={openCreateCardModal} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-info-card-btn">
              <Plus className="h-4 w-4" />
              Bilgi Kartı Ekle
            </Button>
          )}
          <Button onClick={() => { loadCards(); loadDocumentForms(); }} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="bilgiler" className="gap-2" data-testid="tab-bilgiler">
            <Building2 className="h-4 w-4" />
            BİLGİ KARTLARI
          </TabsTrigger>
          <TabsTrigger value="belgeler" className="gap-2" data-testid="tab-belgeler">
            <FolderOpen className="h-4 w-4" />
            BELGELER
            {personelForms.length > 0 && (
              <Badge className="ml-1 bg-blue-500 text-white">{personelForms.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* BİLGİ KARTLARI Tab */}
        <TabsContent value="bilgiler" className="mt-4">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-700">
                Bu sayfada şube yöneticinizin paylaştığı önemli bilgileri ve belgeleri görüntüleyebilirsiniz.
                Dosyaları önizleyebilir veya indirebilirsiniz.
              </p>
            </div>
          </div>

          {/* Cards */}
          {cards.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                <h3 className="text-xl font-bold mb-2">Henüz Bilgi Kartı Yok</h3>
                <p className="text-gray-500 mb-4">
                  Şube yöneticiniz henüz bir bilgi kartı paylaşmamış.
                </p>
                {isBranchManager && (
                  <Button onClick={openCreateCardModal} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-info-card-empty-btn">
                    <Plus className="h-4 w-4" />
                    Bilgi Kartı Ekle
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cards.map(card => (
                <Card 
                  key={card.id} 
                  className={`hover:shadow-lg transition-shadow ${card.is_new ? 'ring-2 ring-red-400 ring-inset bg-red-50' : ''}`} 
                  data-testid={`info-card-${card.id}`}
                  onClick={() => markCardAsSeen(card)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg text-blue-700">{card.title}</CardTitle>
                        {card.is_new && (
                          <Badge className="bg-red-500 text-white text-xs animate-pulse">YENİ</Badge>
                        )}
                      </div>
                      {/* İş Paneli'nde düzenleme/silme yetkisi yok - sadece şube yöneticileri */}
                      {isBranchManager && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditCardModal(card); }} data-testid={`edit-card-${card.id}`}>
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.id); }} data-testid={`delete-card-${card.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* Alıcı Bilgisi */}
                    <div className="flex items-center gap-1 mt-1">
                      {card.recipient_type === 'all' ? (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <Users className="h-3 w-3 mr-1" />
                          Tüm Çalışanlar
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                          <User className="h-3 w-3 mr-1" />
                          {card.recipient_ids?.length || 0} Kişi
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4 whitespace-pre-wrap">{card.description || 'Açıklama yok'}</p>
                    
                    {/* Files */}
                    {card.files && card.files.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Ekli Dosyalar ({card.files.length})</p>
                        {card.files.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm border">
                            <div className="flex items-center gap-2 truncate flex-1">
                              {getFileIcon(file.type)}
                              <div className="truncate">
                                <span className="font-medium truncate block">{file.filename || file.name}</span>
                                <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 px-2"
                                onClick={() => setPreviewFile({...file, filename: file.filename || file.name, url: file.data || file.url})}
                                data-testid={`preview-file-${idx}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Önizle
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 px-2"
                                onClick={() => handleDownload(file)}
                                data-testid={`download-file-${idx}`}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                İndir
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-400 mt-4 pt-2 border-t flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Yayınlanma: {card.publish_date 
                        ? new Date(card.publish_date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
                        : new Date(card.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })
                      }
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* BELGELER Tab */}
        <TabsContent value="belgeler" className="mt-4">
          {/* Info Banner */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <FolderOpen className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm text-green-700">
                Tüm talepleriniz için burada bulunan Talep Formlarından yararlanabilirsiniz. 
                Talep Formları doldurulduktan sonra mutlaka imzalanıp talebin içine eklenmelidir.
              </p>
            </div>
          </div>

          {/* Arama Çubuğu */}
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Belge ara... (başlık, açıklama)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-belgeler-input"
            />
          </div>

          {loadingDocs ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : (filteredPersonelForms.length === 0 && searchTerm.trim()) ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Search className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                <h3 className="text-xl font-bold mb-2">Arama Sonucu Bulunamadı</h3>
                <p className="text-gray-500 mb-4">
                  "{searchTerm}" için sonuç yok
                </p>
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Aramayı Temizle
                </Button>
              </CardContent>
            </Card>
          ) : (personelForms.length === 0) ? (
            <Card>
              <CardContent className="p-8 text-center">
                <FolderOpen className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                <h3 className="text-xl font-bold mb-2">Henüz Belge Yok</h3>
                <p className="text-gray-500">
                  İnsan Kaynakları henüz bir belge formu paylaşmamış.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Personel Talep Formları Arşivi */}
              {filteredPersonelForms.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    Personel Talep Formları Arşivi ({filteredPersonelForms.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredPersonelForms.map(form => (
                      <Card key={form.id} className="hover:shadow-lg transition-shadow border-green-200" data-testid={`personel-form-${form.id}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg text-green-700 flex items-center gap-2">
                              <FileText className="h-5 w-5" />
                              {form.title}
                            </CardTitle>
                            <Badge className="bg-green-100 text-green-700">
                              {form.documents?.length || 0} Dosya
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {form.description && (
                            <p className="text-gray-600 text-sm mb-4">{form.description}</p>
                          )}
                          
                          {/* Documents */}
                          {form.documents && form.documents.length > 0 && (
                            <div className="space-y-2">
                              {form.documents.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                      {getFileIcon(doc.type)}
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">{doc.name}</p>
                                      <p className="text-xs text-gray-400">
                                        {doc.type?.split('/')[1]?.toUpperCase() || 'Dosya'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    {doc.data && (
                                      <>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            const newWindow = window.open();
                                            if (newWindow) {
                                              newWindow.document.write(`
                                                <html>
                                                  <head><title>${doc.name}</title></head>
                                                  <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                                    ${doc.type?.startsWith('image/') 
                                                      ? `<img src="${doc.data}" style="max-width:100%;max-height:100vh;" />`
                                                      : doc.type === 'application/pdf'
                                                        ? `<iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe>`
                                                        : `<div style="padding:20px;background:white;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);"><p>Bu dosya türü önizlenemez.</p><p>Lütfen indirin.</p></div>`
                                                    }
                                                  </body>
                                                </html>
                                              `);
                                            }
                                          }}
                                          className="gap-1"
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="default"
                                          size="sm"
                                          onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = doc.data;
                                            link.download = doc.name;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            toast.success(`${doc.name} indiriliyor...`);
                                          }}
                                          className="gap-1 bg-green-600 hover:bg-green-700"
                                        >
                                          <Download className="h-4 w-4" />
                                          İndir
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-400 mt-4 pt-2 border-t">
                            Paylaşan: {form.created_by_name || 'İK'} • {new Date(form.created_at).toLocaleDateString('tr-TR')}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* File Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-8">
              <div className="flex items-center gap-2 truncate">
                {previewFile && getFileIcon(previewFile.type)}
                <span className="truncate">{previewFile?.filename}</span>
              </div>
              <Button variant="default" size="sm" onClick={() => handleDownload(previewFile)} className="bg-blue-600 hover:bg-blue-700">
                <Download className="h-4 w-4 mr-2" />
                İndir
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-auto max-h-[70vh] bg-gray-100 rounded-lg p-4">
            {previewFile?.type?.includes('image') ? (
              <img src={previewFile.url} alt={previewFile.filename} className="max-w-full mx-auto rounded shadow-lg" />
            ) : previewFile?.type?.includes('pdf') ? (
              <iframe src={previewFile.url} className="w-full h-[60vh] rounded" title={previewFile.filename} />
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <AlertCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">Bu dosya türü tarayıcıda önizlenemez.</p>
                <p className="text-sm text-gray-400 mb-4">Dosyayı görüntülemek için indirin.</p>
                <Button onClick={() => handleDownload(previewFile)} className="bg-blue-600 hover:bg-blue-700">
                  <Download className="h-4 w-4 mr-2" />
                  Dosyayı İndir
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bilgi Kartı Ekleme/Düzenleme Modal */}
      <Dialog open={cardModalOpen} onOpenChange={setCardModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Building2 className="h-5 w-5" />
              {editingCard ? 'Bilgi Kartını Düzenle' : 'Yeni Bilgi Kartı Oluştur'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Konu */}
            <div className="space-y-2">
              <Label htmlFor="card-title">Konu *</Label>
              <Input
                id="card-title"
                value={cardFormData.title}
                onChange={(e) => setCardFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Bilgi kartı konusu..."
                data-testid="card-title-input"
              />
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label htmlFor="card-description">Açıklama</Label>
              <Textarea
                id="card-description"
                value={cardFormData.description}
                onChange={(e) => setCardFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detaylı açıklama yazın..."
                rows={4}
                data-testid="card-description-input"
              />
            </div>

            {/* Yayınlama Tarihi */}
            <div className="space-y-2">
              <Label htmlFor="card-publish-date">Yayınlama Tarihi</Label>
              <Input
                id="card-publish-date"
                type="date"
                value={cardFormData.publish_date}
                onChange={(e) => setCardFormData(prev => ({ ...prev, publish_date: e.target.value }))}
                data-testid="card-publish-date-input"
              />
            </div>

            {/* Alıcı Seçimi */}
            <div className="space-y-3">
              <Label>Alıcılar</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    checked={cardFormData.recipient_type === 'all'}
                    onChange={() => setCardFormData(prev => ({ ...prev, recipient_type: 'all', recipient_ids: [] }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <Users className="h-4 w-4 text-green-600" />
                  <span>Tüm Çalışanlar</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    checked={cardFormData.recipient_type === 'specific'}
                    onChange={() => setCardFormData(prev => ({ ...prev, recipient_type: 'specific' }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <User className="h-4 w-4 text-purple-600" />
                  <span>Belirli Kişiler</span>
                </label>
              </div>

              {cardFormData.recipient_type === 'specific' && (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-600 mb-2">Kişi seçin ({cardFormData.recipient_ids.length} seçili)</p>
                  <ScrollArea className="h-[150px]">
                    <div className="space-y-2">
                      {branchEmployees.map(emp => (
                        <label key={emp.id} className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                          <Checkbox
                            checked={cardFormData.recipient_ids.includes(emp.id)}
                            onCheckedChange={() => toggleRecipient(emp.id)}
                          />
                          <span className="text-sm">{emp.name}</span>
                          {emp.position && <span className="text-xs text-gray-400">({emp.position})</span>}
                        </label>
                      ))}
                      {branchEmployees.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">Çalışan bulunamadı</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>

            {/* Belgeler */}
            <div className="space-y-2">
              <Label>Belgeler</Label>
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="card-file-upload"
                  data-testid="card-file-input"
                />
                <label htmlFor="card-file-upload" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-blue-500 mb-2" />
                  <span className="text-sm text-blue-700 font-medium">Belge eklemek için tıklayın</span>
                  <span className="text-xs text-blue-500">PDF, Word, Excel, Resim vb.</span>
                </label>
              </div>

              {/* Mevcut Dosyalar */}
              {cardFormData.files.length > 0 && (
                <div className="space-y-2 mt-2">
                  <p className="text-xs text-gray-500">Mevcut Dosyalar:</p>
                  {cardFormData.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded border">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.type)}
                        <span className="text-sm truncate max-w-[200px]">{file.filename || file.name}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeExistingFile(index)} className="h-6 w-6 p-0 text-red-500">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Yeni Eklenen Dosyalar */}
              {pendingFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  <p className="text-xs text-gray-500">Yeni Eklenecek Dosyalar:</p>
                  {pendingFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.type)}
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <Badge className="bg-green-100 text-green-700 text-xs">Yeni</Badge>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removePendingFile(index)} className="h-6 w-6 p-0 text-red-500">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setCardModalOpen(false)} disabled={savingCard}>
              İptal
            </Button>
            <Button onClick={handleSaveCard} disabled={savingCard} className="bg-blue-600 hover:bg-blue-700" data-testid="save-card-btn">
              {savingCard ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  {editingCard ? 'Güncelle' : 'Oluştur'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShubeBilgileri;
